/**
 * The read and the writes (1.O-1 deliverable 3), pure. `readWhen` parses the phrase with 1.K-1's
 * engine exactly as `chrono.preview` does; `toPlacementDates` turns a read into a placement's
 * dates; `planWrites` orders the route calls (`commit.ts` runs them); `anchorBounds`,
 * `whenToBoundary` and `refusalMessage` serve the preview, 1.J and 1.Y, and the editor's alerts.
 */
import {
  add,
  anchorEdge,
  type CompiledCalendar,
  formatTimePoint,
  materialize,
  type ParsedWhen,
  parse,
  preview,
  type WhenPreview,
} from '@codex/calendar';
import {
  type AnchorChoiceV1,
  ApiError,
  type ChronRelation,
  type DatedPointV1,
  type LinkBoundaryV1,
  NetworkError,
  type ParticipantV1,
  type PlacementKind,
  type PlacementStatus,
  type PlacementV1,
  type Precision,
  type TimePointParts,
  type TimePointV1,
  type TimeUnit,
  type WhenKind,
} from '@codex/contracts';
import { type AnchorToken, docIdForFid, serializePhrase } from './phrase.js';
import { REFUSALS, roleWord } from './words.js';

export interface WhenCtx {
  readonly calendar: CompiledCalendar;
  readonly now: number | null;
}

export interface WhenRead {
  /** The phrase as typed, and as the grammar reads it (anchors as `@[Label](ref:<fid>)`). */
  readonly text: string;
  readonly serialized: string;
  /** What is stored: `materialize`d (a `now` phrase is a point here). */
  readonly parsed: ParsedWhen;
  /** The kind as typed (`now` stays `now`). */
  readonly kind: WhenKind;
  readonly preview: WhenPreview;
  /** A relative read's anchor page, from its fid; null when the GM has not picked it. */
  readonly anchorDocId: string | null;
}

type Point = Extract<ParsedWhen, { kind: 'point' }>;
type Relative = Extract<ParsedWhen, { kind: 'relative' }>;

export function readWhen(text: string, anchors: readonly AnchorToken[], ctx: WhenCtx): WhenRead {
  const serialized = serializePhrase(text, anchors);
  const raw = parse(serialized, { calendar: ctx.calendar, now: ctx.now });
  const parsed =
    raw.kind === 'point' || raw.kind === 'now'
      ? materialize(raw, ctx.calendar, { now: ctx.now })
      : raw;
  const shown = raw.kind === 'point' ? parsed : raw;
  return {
    text,
    serialized,
    parsed,
    kind: raw.kind,
    preview: preview(shown, ctx.calendar, { now: ctx.now }),
    anchorDocId: raw.kind === 'relative' ? docIdForFid(anchors, raw.anchor.fid) : null,
  };
}

/** Whether a read can be saved: not `invalid`, its ticks valid, and any anchor picked. */
export function isSavable(read: WhenRead): boolean {
  if (read.parsed.kind === 'invalid' || read.preview.status === 'invalid') return false;
  return read.parsed.kind !== 'relative' || read.anchorDocId !== null;
}

const isOpen = (parts: TimePointParts | null) =>
  parts !== null && Object.values(parts).every((value) => value === undefined);

/** "Lasts the whole range" is offered for a two-sided point and one open above ("since …"). */
export function lastsAllowed(parsed: ParsedWhen): boolean {
  return parsed.kind === 'point' && parsed.hiParts !== null && !isOpen(parsed.loParts);
}

export interface PlacementDates {
  readonly kind: PlacementKind;
  readonly start: TimePointV1 | null;
  readonly end: TimePointV1 | null;
  readonly ongoing: boolean;
}

export interface DatesOptions {
  readonly lasts: boolean;
  readonly rawInput: string;
  readonly calendar: CompiledCalendar;
}

function timePoint(
  calendar: CompiledCalendar,
  p: Pick<Point, 'loParts' | 'hiParts' | 'precision' | 'qualifier'>,
  rawInput: string,
): TimePointV1 {
  const { loParts, hiParts, precision, qualifier } = p;
  const label = formatTimePoint(calendar, p) ?? rawInput;
  return { loParts, hiParts, precision, qualifier, label, rawInput };
}

/** A read as a placement's dates; null for `invalid` (Save is disabled). */
export function toPlacementDates(parsed: ParsedWhen, options: DatesOptions): PlacementDates | null {
  const { calendar, rawInput } = options;
  if (parsed.kind === 'invalid') return null;
  if (parsed.kind !== 'point') return { kind: 'undated', start: null, end: null, ongoing: false };
  const { precision, qualifier } = parsed;
  if (options.lasts && lastsAllowed(parsed) && parsed.hiParts !== null) {
    const side = (parts: TimePointParts) =>
      timePoint(calendar, { loParts: parts, hiParts: null, precision, qualifier }, rawInput);
    const start = side(parsed.loParts);
    if (isOpen(parsed.hiParts)) return { kind: 'span', start, end: null, ongoing: true };
    return { kind: 'span', start, end: side(parsed.hiParts), ongoing: false };
  }
  return {
    kind: 'moment',
    start: timePoint(calendar, parsed, rawInput),
    end: null,
    ongoing: false,
  };
}

// ------------------------------------------------------------------------------- the plan

export interface PlacementExtra {
  readonly label?: string;
  readonly summary?: string;
  readonly anchorBlockId?: string;
  readonly participants?: readonly ParticipantV1[];
}

export interface WriteTarget {
  readonly docId: string;
  /** The placement the editor edits; null makes one. */
  readonly placement: PlacementV1 | null;
  readonly role: string;
  readonly status: PlacementStatus;
  readonly lasts: boolean;
  readonly calendar: CompiledCalendar;
  /** Mark as event's fields, on a new placement. */
  readonly extra?: PlacementExtra;
}

/** The GM's choice among an anchor's placements (`pick`), and a session's placement. */
export interface WriteChoice {
  readonly anchorPlacementId?: string | null;
  readonly sessionPlacementId?: string | null;
}

/** The anchor placement a When input's choice settles on: `use` carries its id, `pick` the GM's pick. */
export function anchorPlacementOf(
  choice: AnchorChoiceV1 | null,
  picked: string | null,
): string | null {
  return choice?.kind === 'use' ? choice.id : choice?.kind === 'pick' ? picked : null;
}

/** A placement or a page on the other side of a constraint. */
export type OtherSide = { readonly placementId: string } | { readonly docId: string };

export type PlanStep =
  | {
      readonly op: 'placements.create';
      readonly docId: string;
      readonly body: Record<string, unknown> & { kind: PlacementKind };
    }
  | {
      readonly op: 'placements.update';
      readonly docId: string;
      readonly placementId: string;
      readonly body: Record<string, unknown> & { baseRv: number };
    }
  | {
      readonly op: 'constraints.create';
      readonly docId: string;
      /** This placement's id; null: the one an earlier step created. */
      readonly self: string | null;
      readonly selfSide: 'before' | 'after';
      readonly other: OtherSide;
      readonly relation: ChronRelation;
      readonly lag: { minLagN?: number; maxLagN?: number; lagUnit: TimeUnit } | null;
    };

export type Plan = { readonly steps: PlanStep[] } | { readonly refusal: string };

const RELATIONS: Readonly<
  Record<Relative['relation'], { selfSide: 'before' | 'after'; relation: ChronRelation }>
> = {
  after: { selfSide: 'after', relation: 'before' },
  before: { selfSide: 'before', relation: 'before' },
  during: { selfSide: 'after', relation: 'during' },
  same_time: { selfSide: 'after', relation: 'same_time' },
};

function datesBody(dates: PlacementDates): Record<string, unknown> & { kind: PlacementKind } {
  return { kind: dates.kind, start: dates.start, end: dates.end, ongoing: dates.ongoing };
}

function createBody(target: WriteTarget, dates: PlacementDates) {
  const { extra } = target;
  const body: Record<string, unknown> & { kind: PlacementKind } = {
    ...datesBody(dates),
    role: target.role,
    status: target.status,
  };
  if (dates.start === null) delete body.start;
  if (dates.end === null) delete body.end;
  if (extra?.label) body.label = extra.label;
  if (extra?.summary) body.summary = extra.summary;
  if (extra?.anchorBlockId) body.anchorBlockId = extra.anchorBlockId;
  if (extra?.participants) body.participants = [...extra.participants];
  return body;
}

/** The constraint a relative or session read asks for, or a refusal. */
function constraintOf(
  read: WhenRead,
  target: WriteTarget,
  choice: WriteChoice,
  self: string | null,
): PlanStep | string {
  const { parsed } = read;
  if (parsed.kind === 'session') {
    const id = choice.sessionPlacementId;
    if (!id) return REFUSALS.noSession;
    const other = { placementId: id };
    return {
      op: 'constraints.create',
      docId: target.docId,
      self,
      selfSide: 'after',
      other,
      relation: 'same_time',
      lag: null,
    };
  }
  const relative = parsed as Relative;
  const picked = choice.anchorPlacementId;
  let other: OtherSide;
  if (picked) other = { placementId: picked };
  else if (read.anchorDocId !== null) other = { docId: read.anchorDocId };
  else return REFUSALS.pick;
  const { lag } = relative;
  return {
    op: 'constraints.create',
    docId: target.docId,
    self,
    ...RELATIONS[relative.relation],
    other,
    lag:
      lag === null
        ? null
        : {
            ...(lag.min === null ? {} : { minLagN: lag.min }),
            ...(lag.max === null ? {} : { maxLagN: lag.max }),
            lagUnit: lag.unit,
          },
  };
}

/**
 * The ordered writes. A dated or undated read updates the placement, or creates one. A relative
 * or session read on a dated placement creates the constraint first and then makes the placement
 * undated; on an undated placement it only creates the constraint; on a page without one it
 * creates the undated placement first. `commit.ts` undoes an earlier create when a later step fails.
 */
export function planWrites(read: WhenRead, target: WriteTarget, choice: WriteChoice = {}): Plan {
  const dates = toPlacementDates(read.parsed, {
    lasts: target.lasts,
    rawInput: read.text,
    calendar: target.calendar,
  });
  if (dates === null || !isSavable(read)) return { refusal: 'invalid' };
  const { placement, docId } = target;
  const update = (d: PlacementDates): PlanStep => ({
    op: 'placements.update',
    docId,
    placementId: (placement as PlacementV1).id,
    body: { baseRv: (placement as PlacementV1).rv, ...datesBody(d), status: target.status },
  });
  const relative = read.parsed.kind === 'relative' || read.parsed.kind === 'session';
  if (!relative) {
    if (placement === null) {
      return { steps: [{ op: 'placements.create', docId, body: createBody(target, dates) }] };
    }
    return { steps: [update(dates)] };
  }
  const constraint = constraintOf(read, target, choice, placement?.id ?? null);
  if (typeof constraint === 'string') return { refusal: constraint };
  if (placement === null) {
    return {
      steps: [{ op: 'placements.create', docId, body: createBody(target, dates) }, constraint],
    };
  }
  if (placement.kind === 'undated') return { steps: [constraint] };
  return { steps: [constraint, update(dates)] };
}

// ------------------------------------------------------------------------- anchor bounds

interface Range {
  readonly lo: number | null;
  readonly hi: number | null;
}

function rangeOf(point: DatedPointV1 | null): Range | null {
  if (point === null) return null;
  const t = point.ticks;
  if (t.status === 'ok') return { lo: t.lo, hi: t.hi };
  if (t.status === 'open_lo') return { lo: null, hi: t.hi };
  if (t.status === 'open_hi') return { lo: t.lo, hi: null };
  return null;
}

export interface AnchorBounds {
  /** Inclusive; null: open. */
  readonly earliest: number | null;
  /** Exclusive; null: open. */
  readonly latest: number | null;
  readonly precision: Precision;
}

const LAG_PRECISION: Readonly<Record<TimeUnit, Precision>> = {
  minute: 'minute',
  hour: 'hour',
  day: 'day',
  week: 'day',
  month: 'day',
  year: 'day',
};

/**
 * Where a relative read can fall, from the anchor edge (`anchorEdge`) of the anchor placement:
 * its ticks where dated, else the solver's `boundLo`/`boundHi`, moved by the minimum and maximum
 * lag through `add`. Null when nothing bounds it (an undated anchor nothing orders yet).
 */
export function anchorBounds(
  read: WhenRead,
  anchor: PlacementV1,
  calendar: CompiledCalendar,
): AnchorBounds | null {
  if (read.parsed.kind !== 'relative') return null;
  const { relation, lag } = read.parsed;
  const solved: Range = { lo: anchor.boundLo, hi: anchor.boundHi };
  const start = rangeOf(anchor.start) ?? solved;
  const endPoint = anchor.end ?? (anchor.kind === 'moment' ? anchor.start : null);
  // An ongoing span has not ended: its end edge is no earlier than its start, open above.
  const end = anchor.ongoing ? { lo: start.lo, hi: null } : (rangeOf(endPoint) ?? solved);
  const edge = anchorEdge(relation);
  const at: Range =
    edge === 'span'
      ? { lo: solved.lo ?? start.lo, hi: solved.hi ?? end.hi }
      : edge === 'end'
        ? end
        : start;
  if (at.lo === null && at.hi === null) return null;
  const move = (tick: number | null, n: number | null): number | null => {
    if (tick === null || n === null) return null;
    if (lag === null || n === 0) return tick;
    const moved = add(calendar, tick, n, lag.unit);
    return moved.ok ? moved.tick : null;
  };
  const min = lag?.min ?? 0;
  const max = lag === null ? null : lag.max;
  let earliest: number | null;
  let latest: number | null;
  if (relation === 'after') {
    earliest = move(at.lo, min);
    latest = move(at.hi, max);
  } else if (relation === 'before') {
    earliest = max === null ? null : move(at.lo, -max);
    latest = move(at.hi, -min);
  } else {
    earliest = at.lo;
    latest = at.hi;
  }
  const precision = lag ? LAG_PRECISION[lag.unit] : (anchor.start?.point.precision ?? 'day');
  return { earliest, latest, precision };
}

/**
 * Whether a read's kind may be used where the caller restricts the kinds. An invalid read is never
 * "the wrong kind": its own parse reason shows instead (and it is not savable anyway).
 */
export function kindAllowed(read: WhenRead, allow: readonly WhenKind[] | undefined): boolean {
  return allow === undefined || read.kind === 'invalid' || allow.includes(read.kind);
}

// ------------------------------------------------------------------------- 1.J and 1.Y

export type BoundaryResult =
  | { readonly ok: true; readonly boundary: LinkBoundaryV1 }
  | { readonly ok: false; readonly reason: string };

/**
 * A read as one end of a link's or a state's validity: a point as its time point, "after X" as X's
 * end edge and "before X" as X's start edge (X's placement chosen); anything else a reason.
 */
export function whenToBoundary(
  read: WhenRead,
  choice: WriteChoice & { readonly calendar: CompiledCalendar },
): BoundaryResult {
  const { parsed } = read;
  if (!isSavable(read)) return { ok: false, reason: "This date can't be read." };
  if (parsed.kind === 'point') {
    return { ok: true, boundary: { timePoint: timePoint(choice.calendar, parsed, read.text) } };
  }
  if (parsed.kind === 'relative') {
    if (parsed.lag !== null) return { ok: false, reason: 'A boundary takes no gap.' };
    const id = choice.anchorPlacementId;
    if (!id) return { ok: false, reason: REFUSALS.pick };
    if (parsed.relation === 'after')
      return { ok: true, boundary: { placementId: id, edge: 'end' } };
    if (parsed.relation === 'before') {
      return { ok: true, boundary: { placementId: id, edge: 'start' } };
    }
    return { ok: false, reason: 'A boundary is "after" or "before" a page.' };
  }
  if (parsed.kind === 'undated') return { ok: false, reason: 'An undated phrase bounds nothing.' };
  return { ok: false, reason: 'A session number bounds nothing here.' };
}

export interface Refusal {
  readonly message: string;
  readonly conflict: boolean;
  /** A 422 `pick`: the anchor's options, for the picker. */
  readonly anchorChoice: AnchorChoiceV1 | null;
}

const refusal = (message: string, extra: Partial<Refusal> = {}): Refusal => ({
  message,
  conflict: false,
  anchorChoice: null,
  ...extra,
});

/** A failed write in words. */
export function refusalMessage(error: unknown, role = 'occurs'): Refusal {
  if (error instanceof NetworkError) return refusal(REFUSALS.network);
  if (!(error instanceof ApiError)) return refusal(REFUSALS.other);
  const details = (error.body.details ?? {}) as Record<string, unknown>;
  if (error.code === 'row_conflict') return refusal(REFUSALS.conflict, { conflict: true });
  if (error.code === 'document_trashed') return refusal(REFUSALS.trashed);
  if (error.status === 404) return refusal(REFUSALS.notFound);
  if (error.status === 422 && error.code === 'invalid_body') {
    const choice = details.anchorChoice as AnchorChoiceV1 | undefined;
    if (choice?.kind === 'pick') return refusal(REFUSALS.pick, { anchorChoice: choice });
    if (typeof details.placementId === 'string' || details.reason === 'canon_role_taken') {
      return refusal(REFUSALS.canonRole.replace('{role}', roleWord(role)));
    }
    if (details.reason === 'anchor_block_missing') return refusal(REFUSALS.blockUnsaved);
    if (details.reason === 'lag_not_allowed') return refusal(REFUSALS.lag);
    return refusal(REFUSALS.invalid);
  }
  return refusal(REFUSALS.other);
}
